// features/reports/components/PresupuestoPeriodo.tsx
// ============================================================================
// PRONÓSTICO Y PRESUPUESTO POR TRIMESTRE / SEMESTRE (Mabel 23-oct-2026). No calcula nada: el servidor
// (/finances/presupuesto/periodo, lib/letsmovePeriodo.js) corre el reporte mensual de cada mes y suma, así que el
// total cuadra con lo que se ve mes por mes. Meses que aún no empiezan entran con su pronóstico tentativo (marcados).
// ============================================================================
import { Fragment, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import { getPresupuestoPeriodo, PresupuestoPeriodo as PP, TipoPeriodo, PeriodoColumna } from '../api';
import { moneyEntero as money } from '../../finances/api';
import { readCache, writeCache } from '../../../services/localCache';
import CountUp from '../../../components/CountUp';
import { CargandoPeriodo, Actualizando } from './PeriodoSelector';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const colorRes = (n: number | null) => (n == null ? 'text-content-3' : n >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400');
const pctTxt = (p: number | null) => (p == null ? '' : `${p >= 0 ? '+' : '−'}${Math.abs(p).toFixed(1)} %`);
const celda = (v: number | null | undefined) => (v == null ? '—' : money(v));
const th = 'px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide text-content-3 whitespace-nowrap';
const td = 'px-3 py-1.5 text-right tabular-nums whitespace-nowrap';

function CabMes({ c }: { c: PeriodoColumna }) {
  return (
    <th className={th}>
      {c.corto}
      {c.estado !== 'cerrado' && (
        <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] normal-case ${c.estado === 'pronostico' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' : 'bg-surface-2 text-content-2'}`}>
          {c.estado === 'pronostico' ? 'pronóstico' : 'en curso'}
        </span>
      )}
    </th>
  );
}

export default function PresupuestoPeriodo({ tipo, mes, movil = false }: { tipo: TipoPeriodo; mes: string; movil?: boolean }) {
  const ck = `rep-presupuesto-${tipo}-${mes}${movil ? '-movil' : ''}`;
  const [d, setD] = useState<PP | null>(() => readCache<PP>(ck) ?? null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const toggle = (k: string) => setAbiertos((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  useEffect(() => {
    let vivo = true;
    const cached = readCache<PP>(ck);
    setD(cached ?? null); setError(null); setCargando(!cached);
    getPresupuestoPeriodo(tipo, mes, movil)
      .then((r) => { if (!vivo) return; setD(r); writeCache(ck, r); })
      .catch((e: any) => { if (vivo) setError(e?.message || 'No se pudo cargar el periodo'); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [tipo, mes, movil]);

  if (error && !d) return <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300">{error}</p>;
  if (!d) return <CargandoPeriodo tipo={tipo} mes={mes} movil={movil} />;

  const fila = (k: string) => d.resumen.find((f) => f.k === k)!;
  const conReal = d.hayFuturos;
  const kpi = (k: string) => (conReal ? fila(k).totalReal : fila(k).total);
  const ingReal = fila('ingresosReal'), res = fila('resultado');
  const ingTotales = {
    presupuesto: d.ingresos.reduce((s, f) => s + (f.presupuesto ?? 0), 0),
    real: d.ingresos.reduce((s, f) => s + f.real, 0),
    variacion: d.ingresos.reduce((s, f) => s + (f.variacion ?? 0), 0),
  };

  return (
    <div className={cargando ? 'space-y-4 opacity-60 transition' : 'space-y-4 transition'} data-testid="presupuesto-periodo">
      {cargando && <div className="flex justify-end"><Actualizando /></div>}
      <p className="text-sm text-content-3">
        <b className="text-content-2">{d.titulo}</b> · suma de {d.columnas.map((c) => c.corto).join(', ')}
        {conReal && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">incluye pronóstico</span>}
      </p>

      {/* KPI del periodo (con meses futuros: solo lo real a la fecha) */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Ingresos reales</div>
          <div className="mt-1 font-display text-xl font-bold text-content"><CountUp value={ingReal.total} format={money} /></div>
          <div className="mt-0.5 text-xs text-content-3">devengado del periodo</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Cobrado en caja</div>
          <div className="mt-1 font-display text-xl font-bold text-content"><CountUp value={fila('caja').total} format={money} /></div>
          <div className="mt-0.5 text-xs text-content-3">todos los cobros del periodo</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Gastos + nómina</div>
          <div className="mt-1 font-display text-xl font-bold text-content"><CountUp value={kpi('gastos') + kpi('nominaCalculada') + kpi('inversiones')} format={money} /></div>
          <div className="mt-0.5 text-xs text-content-3">nómina calculada{conReal ? ', real a la fecha' : ''}</div>
        </div>
        <div className={card}>
          <div className="text-xs font-medium uppercase tracking-wide text-content-3">Resultado{conReal ? ' a la fecha' : ''}</div>
          <div className={`mt-1 font-display text-xl font-bold ${colorRes(kpi('resultado'))}`}><CountUp value={kpi('resultado')} format={money} /></div>
          <div className="mt-0.5 text-xs text-content-3">{conReal ? `con pronóstico: ${money(res.total)}` : 'con la nómina calculada'}</div>
        </div>
      </div>

      {/* Mes por mes */}
      <div className={`${card} overflow-x-auto p-0`}>
        <h3 className="px-4 pt-4 pb-2 font-display text-base font-bold text-content">Mes por mes</h3>
        <table className="w-full text-sm" data-testid="periodo-resumen">
          <thead>
            <tr className="border-b border-line">
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-3">Concepto</th>
              {d.columnas.map((c) => <CabMes key={c.mes} c={c} />)}
              <th className={th}>Total</th>
              {conReal && <th className={th}>Real a la fecha</th>}
            </tr>
          </thead>
          <tbody>
            {d.resumen.map((f) => (
              <tr key={f.k} className={`border-b border-line/60 ${f.resultado ? 'bg-surface-2 font-semibold' : ''}`}>
                <td className="px-4 py-1.5 text-content-2">{f.label}</td>
                {d.columnas.map((c) => <td key={c.mes} className={`${td} ${f.resultado ? colorRes(f.valores[c.mes]) : 'text-content'} ${c.estado === 'pronostico' ? 'italic' : ''}`}>{celda(f.valores[c.mes])}</td>)}
                <td className={`${td} font-semibold ${f.resultado ? colorRes(f.total) : 'text-content'}`}>{money(f.total)}</td>
                {conReal && <td className={`${td} ${f.resultado ? colorRes(f.totalReal) : 'text-content-2'}`}>{money(f.totalReal)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Ingresos por concepto: presupuesto vs real del periodo */}
      <div className={`${card} overflow-x-auto p-0`}>
        <h3 className="px-4 pt-4 pb-2 font-display text-base font-bold text-content">Ingresos · Presupuesto vs Real del periodo</h3>
        <table className="w-full text-sm" data-testid="periodo-ingresos">
          <thead>
            <tr className="border-b border-line">
              <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-3">Concepto</th>
              {d.columnas.map((c) => <CabMes key={c.mes} c={c} />)}
              <th className={th}>Presupuesto</th><th className={th}>Real</th><th className={th}>Variación</th>
            </tr>
          </thead>
          <tbody>
            {d.ingresos.map((f) => (
              <tr key={f.k} className="border-b border-line/60">
                <td className="px-4 py-1.5 text-content-2">{f.categoria} · {f.concepto}</td>
                {d.columnas.map((c) => <td key={c.mes} className={`${td} text-content`}>{celda(f.porMes[c.mes]?.real)}</td>)}
                <td className={`${td} text-content-2`}>{celda(f.presupuesto)}</td>
                <td className={`${td} font-semibold text-content`}>{money(f.real)}</td>
                <td className={`${td} ${colorRes(f.variacion)}`}>{f.variacion == null ? '—' : `${money(f.variacion)} ${pctTxt(f.pct)}`}</td>
              </tr>
            ))}
            <tr className="bg-surface-2 font-semibold">
              <td className="px-4 py-1.5 text-content">Total</td>
              {d.columnas.map((c) => <td key={c.mes} className={`${td} text-content`}>{celda(d.ingresos.reduce((s, f) => s + (f.porMes[c.mes]?.real ?? 0), 0))}</td>)}
              <td className={`${td} text-content`}>{money(ingTotales.presupuesto)}</td>
              <td className={`${td} text-content`}>{money(ingTotales.real)}</td>
              <td className={`${td} ${colorRes(ingTotales.variacion)}`}>{money(ingTotales.variacion)}</td>
            </tr>
          </tbody>
        </table>
        <p className="px-4 pb-3 pt-2 text-xs text-content-3">La variación compara lo real contra el presupuesto de los meses ya empezados{conReal ? ' (no contra el pronóstico de los que faltan)' : ''}.</p>
      </div>

      {/* Por apartado */}
      {d.estructura && (
        <div className={`${card} overflow-x-auto p-0`}>
          <h3 className="px-4 pt-4 pb-2 font-display text-base font-bold text-content">Por apartado <span className="text-sm font-normal text-content-3">(real devengado; toca un apartado para ver sus categorías)</span></h3>
          <table className="w-full text-sm" data-testid="periodo-apartados">
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-3">Apartado</th>
                {d.columnas.map((c) => <CabMes key={c.mes} c={c} />)}
                <th className={th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {d.estructura.secciones.map((s) => (
                <Fragment key={s.section}>
                  <tr className="cursor-pointer border-b border-line/60 font-semibold hover:bg-surface-2" onClick={() => toggle(s.section)}>
                    <td className="px-4 py-1.5 text-content">
                      <span className="inline-flex items-center gap-1">{abiertos.has(s.section) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}{s.label}</span>
                    </td>
                    {d.columnas.map((c) => <td key={c.mes} className={`${td} text-content`}>{money(s.valores[c.mes] ?? 0)}</td>)}
                    <td className={`${td} text-content`}>{money(s.total)}</td>
                  </tr>
                  {abiertos.has(s.section) && s.categorias.filter((c) => c.total || Object.values(c.valores).some(Boolean)).map((cat) => (
                    <tr key={`${s.section}-${cat.category_id}`} className="border-b border-line/40">
                      <td className="py-1.5 pl-9 pr-4 text-content-2">{cat.name}</td>
                      {d.columnas.map((c) => <td key={c.mes} className={`${td} text-content-2`}>{money(cat.valores[c.mes] ?? 0)}</td>)}
                      <td className={`${td} text-content-2`}>{money(cat.total)}</td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              <tr className="bg-surface-2 font-semibold">
                <td className="px-4 py-1.5 text-content">Resultado</td>
                {d.columnas.map((c) => <td key={c.mes} className={`${td} ${colorRes(d.estructura!.resultado.valores[c.mes])}`}>{money(d.estructura!.resultado.valores[c.mes] ?? 0)}</td>)}
                <td className={`${td} ${colorRes(d.estructura.resultado.total)}`}>{money(d.estructura.resultado.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Presupuesto de gastos vs real */}
      {d.presupuestoGastos && (
        <div className={`${card} overflow-x-auto p-0`}>
          <h3 className="px-4 pt-4 pb-2 font-display text-base font-bold text-content">Presupuesto de gastos vs real del periodo</h3>
          <table className="w-full text-sm" data-testid="periodo-gastos">
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-content-3">Categoría</th>
                <th className={th}>Presupuesto</th><th className={th}>Real</th><th className={th}>Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {d.presupuestoGastos.categorias.filter((c) => c.presupuesto || c.real).map((c) => (
                <tr key={`${c.apartado}-${c.categoria}`} className="border-b border-line/60">
                  <td className="px-4 py-1.5 text-content-2">{c.apartado} · {c.categoria}</td>
                  <td className={`${td} text-content`}>{money(c.presupuesto)}</td>
                  <td className={`${td} text-content`}>{money(c.real)}</td>
                  <td className={`${td} ${c.diferencia > 0 ? 'text-red-700 dark:text-red-400' : 'text-content-2'}`}>{money(c.diferencia)}</td>
                </tr>
              ))}
              <tr className="bg-surface-2 font-semibold">
                <td className="px-4 py-1.5 text-content">Total</td>
                <td className={`${td} text-content`}>{money(d.presupuestoGastos.totales.presupuesto)}</td>
                <td className={`${td} text-content`}>{money(d.presupuestoGastos.totales.real)}</td>
                <td className={`${td} text-content`}>{money(d.presupuestoGastos.totales.diferencia)}</td>
              </tr>
            </tbody>
          </table>
          <p className="px-4 pb-3 pt-2 text-xs text-content-3">{d.presupuestoGastos.nota}</p>
        </div>
      )}

      <div className="space-y-1 text-xs text-content-3">
        {d.notas.map((n) => <p key={n} className="flex items-start gap-1.5"><Info size={12} className="mt-0.5 shrink-0" />{n}</p>)}
      </div>
    </div>
  );
}
